import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import './BlogPost.css';

const DEFAULT_CONTENT = 'This article is part of our blog on mental health, wellness, and personal development. For more support, explore our services or book an appointment with our team.';

const FALLBACK_IMAGE = 'https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?auto=compress&cs=tinysrgb&w=600';

// Pick initials from an author name. Ignores parenthesized titles like "(Dr)"
// and any leading non-letter characters. "Prof (Dr) Manju Agrawal" → "PA".
function initialsFor(name) {
  if (!name) return 'MS';
  const alphaWords = name
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z]/g, ''))
    .filter(Boolean);
  if (alphaWords.length === 0) return 'MS';
  if (alphaWords.length === 1) return alphaWords[0][0].toUpperCase();
  return (alphaWords[0][0] + alphaWords[alphaWords.length - 1][0]).toUpperCase();
}

function BlogPostLoading() {
  return (
    <div className="blog-post-page">
      <div className="container" style={{ padding: '120px 20px', textAlign: 'center' }}>
        <div className="bp-loading-spinner" />
        <p style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>Loading article...</p>
      </div>
    </div>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const { posts, postsLoading } = useContent();

  const post = !postsLoading && posts ? posts.find((p) => p.slug === slug) : null;

  // SEO: set title + description + OG/Twitter tags when post resolves.
  // Hooks must be unconditional — guard inside the effect body.
  useEffect(() => {
    if (!post) return;

    const baseTitle = post.metaTitle || post.title || 'Mind Spa';
    document.title = `${baseTitle} · Mind Spa`;

    const desc = (post.metaDescription || post.excerpt || '').slice(0, 240);
    const image = post.image || '';
    const url = `${window.location.origin}/blog/${post.slug}`;

    const setMeta = (key, value, kind = 'name') => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${kind}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(kind, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    const setCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    setMeta('description', desc);
    setCanonical(url);

    setMeta('og:type', 'article', 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:title', baseTitle, 'property');
    setMeta('og:description', desc, 'property');
    if (image) {
      setMeta('og:image', image, 'property');
      setMeta('og:image:secure_url', image, 'property');
      setMeta('og:image:alt', baseTitle, 'property');
    }

    setMeta('twitter:title', baseTitle);
    setMeta('twitter:description', desc);
    if (image) setMeta('twitter:image', image);

    return () => { document.title = 'Mind Spa'; };
  }, [post]);

  if (postsLoading) {
    return <BlogPostLoading />;
  }

  if (!post) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="blog-post-not-found">
            <h1>Post not found</h1>
            <p>The blog post you're looking for doesn't exist or may have been removed.</p>
            <Link to="/blogs" className="btn-primary">Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  const relatedPosts = posts.filter((p) => p.slug !== slug).slice(0, 3);

  const content = post.content || DEFAULT_CONTENT;
  const authorName = (post.author && post.author.trim()) || 'MindSpa Team';
  const authorInitials = initialsFor(authorName);
  // Rough read-time: ~200 words per minute on stripped content
  const wordCount = content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
  const readMin = Math.max(1, Math.round(wordCount / 200));
  const tags = Array.isArray(post.tags) ? post.tags : [];

  // Popular tags = top-N most-used tags across every published post.
  const tagFreq = new Map();
  for (const p of posts) {
    for (const t of (p.tags || [])) tagFreq.set(t, (tagFreq.get(t) || 0) + 1);
  }
  const popularTags = Array.from(tagFreq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([t]) => t);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = encodeURIComponent(post.title || 'Mind Spa');

  return (
    <>
      {/* Page Hero Banner */}
      <section className="bp-hero-banner">
        <div className="bp-hero-overlay" />
        <div className="bp-hero-pattern" />
        <div className="container bp-hero-inner">
          <span className="bp-hero-category">{post.category}</span>
          <h1 className="bp-hero-title">{post.title}</h1>
          <div className="bp-hero-meta">
            <span className="bp-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {post.date} {post.month} {post.year || ''}
            </span>
            <span className="bp-meta-sep">&bull;</span>
            <span className="bp-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {authorName}
            </span>
            <span className="bp-meta-sep">&bull;</span>
            <span className="bp-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              {readMin} min read
            </span>
          </div>
          <nav className="bp-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blogs">Blog</Link>
            <span>/</span>
            <span>{post.category}</span>
          </nav>
        </div>
      </section>

      <main className="blog-post-page">
        <div className="container bp-layout">

          {/* Main Article */}
          <article className="bp-article">
            {/* Featured Image */}
            <div className="bp-featured-image">
              <img
                src={post.image || FALLBACK_IMAGE}
                alt={post.title}
                className="bp-featured-img"
              />
            </div>

            {/* Article Body */}
            <div className="bp-body">
              {post.isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                content.split(/\n\n+/).filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))
              )}
            </div>

            {/* Tags — only when the post actually has them */}
            {tags.length > 0 && (
              <div className="bp-tags">
                <span className="bp-tags-label">Tags</span>
                <div className="bp-tags-list">
                  {tags.map((tag) => (
                    <Link key={tag} to={`/tag/${encodeURIComponent(tag)}`} className="bp-tag">
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Share — minimal text-link row */}
            <div className="bp-share">
              <span className="bp-share-label">Share</span>
              <div className="bp-share-row">
                <a
                  className="bp-share-link"
                  target="_blank" rel="noopener noreferrer"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                >Facebook</a>
                <a
                  className="bp-share-link"
                  target="_blank" rel="noopener noreferrer"
                  href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`}
                >Twitter</a>
                <a
                  className="bp-share-link"
                  target="_blank" rel="noopener noreferrer"
                  href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`}
                >WhatsApp</a>
                <a
                  className="bp-share-link"
                  target="_blank" rel="noopener noreferrer"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                >LinkedIn</a>
              </div>
            </div>

            {/* Author Card */}
            <div className="bp-author-card">
              <div className="bp-author-avatar">
                <span>{authorInitials}</span>
              </div>
              <div className="bp-author-info">
                <span className="bp-author-label">Written by</span>
                <h4 className="bp-author-name">{authorName}</h4>
                <p className="bp-author-bio">
                  Our team of certified therapists and wellness experts share
                  evidence-based insights for mental health and well-being.
                </p>
              </div>
            </div>

            {/* Footer nav */}
            <div className="bp-footer-nav">
              <Link to="/blogs" className="btn-outline">&larr; All Posts</Link>
              <Link to="/contact" className="btn-primary">Book Appointment</Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="bp-sidebar">

            {/* Appointment CTA */}
            <div className="bp-sidebar-cta">
              <h3>Need Support?</h3>
              <p>Speak to one of our therapists. The first conversation is the hardest step &mdash; we&apos;ll help you take it.</p>
              <Link to="/contact" className="btn-primary bp-sidebar-btn">Book a Session</Link>
            </div>

            {/* Recent Posts */}
            {relatedPosts.length > 0 && (
              <div className="bp-sidebar-widget">
                <h4 className="bp-widget-title">Recent Posts</h4>
                <ul className="bp-recent-list">
                  {relatedPosts.map((rp) => (
                    <li key={rp.id} className="bp-recent-item">
                      <Link to={`/blog/${rp.slug}`} className="bp-recent-link">
                        <div className="bp-recent-thumb">
                          <img
                            src={rp.image || FALLBACK_IMAGE}
                            alt={rp.title}
                            className="bp-recent-thumb-img"
                          />
                        </div>
                        <div className="bp-recent-info">
                          <span className="bp-recent-date">{rp.date} {rp.month}</span>
                          <span className="bp-recent-title">{rp.title}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Popular Tags — pulled from real post tags */}
            {popularTags.length > 0 && (
              <div className="bp-sidebar-widget">
                <h4 className="bp-widget-title">Popular Tags</h4>
                <div className="bp-tag-cloud">
                  {popularTags.map((tag) => (
                    <Link key={tag} to={`/tag/${encodeURIComponent(tag)}`} className="bp-tag">
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </>
  );
}

export default BlogPost;
